/** IMEI Verify hub services — retail pricing and paid tier (mirrored on backend). */

export const IMEI_HUB_SERVICE_IDS = [
  'verify',
  'passport',
  'lock',
  'carrier',
  'safecheck',
  'business',
  'android',
  'gsx',
  'tmobile',
] as const

export type ImeiHubServiceId = (typeof IMEI_HUB_SERVICE_IDS)[number]

export type ImeiHubCatalogEntry = {
  id: ImeiHubServiceId
  /** Retail price in EUR cents; null = included (free tier). */
  priceEurCents: number | null
  paid: boolean
}

const PAID_SERVICES = new Set<ImeiHubServiceId>(['safecheck', 'lock', 'business', 'gsx', 'tmobile'])

const RETAIL_EUR_CENTS: Partial<Record<ImeiHubServiceId, number>> = {
  safecheck: 149,
  lock: 149,
  business: 199,
  gsx: 699,
  tmobile: 399,
}

export function isImeiHubServiceId(value: string): value is ImeiHubServiceId {
  return (IMEI_HUB_SERVICE_IDS as readonly string[]).includes(value)
}

export function isImeiHubPaidService(service: string): boolean {
  const id = service.trim().toLowerCase()
  return PAID_SERVICES.has(id as ImeiHubServiceId)
}

export function imeiHubRetailPriceEurCents(service: ImeiHubServiceId): number | null {
  if (!isImeiHubPaidService(service)) return null
  return RETAIL_EUR_CENTS[service] ?? null
}

export function buildImeiHubCatalog(): ImeiHubCatalogEntry[] {
  return IMEI_HUB_SERVICE_IDS.map((id) => {
    const cents = imeiHubRetailPriceEurCents(id)
    return { id, priceEurCents: cents, paid: cents != null }
  })
}

export function formatImeiHubPriceEur(cents: number, locale: string): string {
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(cents / 100)
  } catch {
    return `€${(cents / 100).toFixed(2)}`
  }
}
